import React, { useState, useEffect } from 'react';
import {
  Stack,
  Grid,
  Paper,
  Title,
  Text,
  Group,
  RingProgress,
  SimpleGrid,
  Button
} from '@mantine/core';
import { IconCalendar, IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import PageTitle from 'components/PageTitle';
import Badge from 'components/Badge';
import { pathnames } from 'routes/yearlyCalendar';
import { yearlyCalendarService } from 'services/yearlyCalendarService';
import { SchedulePlan } from '../utils/types';

const mockSchedulePlans: SchedulePlan[] = [
  {
    id: 'schedule-1',
    title: 'New Lecture Series',
    description: 'A series of lectures on modern technologies',
    planningName: 'New Lecture Series',
    scheduleType: 'Both',
    scheduleItems: 20,
    startDate: '2025-01-01T00:00:00.000Z',
    endDate: '2025-12-31T23:59:59.999Z',
    lastModifiedBy: 'Lincoln Dokidis',
    status: 'Completed',
    modules: [],
    targetPositions: ['pos-1', 'pos-2'],
    trainingProvider: 'Internal BCL&D',
    createdBy: {
      id: 'user-1',
      name: 'Cooper Korsgaard',
      position: 'Product Mobility Planner'
    }
  },
  {
    id: 'schedule-2',
    title: 'Data Analysis Workshop',
    description: 'Workshop on data analysis techniques',
    planningName: 'Data Analysis Workshop',
    scheduleType: 'Program',
    scheduleItems: 3,
    startDate: '2025-03-15T00:00:00.000Z',
    endDate: '2025-03-20T23:59:59.999Z',
    lastModifiedBy: 'Livia Philips',
    status: 'Unscheduled',
    modules: [],
    targetPositions: ['pos-1'],
    trainingProvider: 'External Provider',
    createdBy: {
      id: 'user-2',
      name: 'Livia Philips',
      position: 'Training Coordinator'
    }
  },
  {
    id: 'schedule-3',
    title: 'Artificial Intelligence Basics',
    description: 'Introduction to AI concepts and applications',
    planningName: 'AI Basics',
    scheduleType: 'Module',
    scheduleItems: 3,
    startDate: '2025-05-10T00:00:00.000Z',
    endDate: '2025-06-10T23:59:59.999Z',
    lastModifiedBy: 'Nolan Siphron',
    status: 'Draft',
    modules: [],
    targetPositions: ['pos-3'],
    trainingProvider: 'Internal BCL&D',
    createdBy: {
      id: 'user-3',
      name: 'Nolan Siphron',
      position: 'AI Specialist'
    }
  },
  {
    id: 'schedule-4',
    title: 'Leadership Development',
    description: 'Comprehensive leadership training program',
    planningName: 'Leadership Development',
    scheduleType: 'Program',
    scheduleItems: 5,
    startDate: '2025-07-01T00:00:00.000Z',
    endDate: '2025-09-30T23:59:59.999Z',
    lastModifiedBy: 'Giana Dorwart',
    status: 'Completed',
    modules: [],
    targetPositions: ['pos-2', 'pos-4'],
    trainingProvider: 'External Provider',
    createdBy: {
      id: 'user-4',
      name: 'Giana Dorwart',
      position: 'HR Director'
    }
  },
  {
    id: 'schedule-5',
    title: 'Digital Marketing Essentials',
    description: 'Core concepts in digital marketing',
    planningName: 'Digital Marketing',
    scheduleType: 'Both',
    scheduleItems: 8,
    startDate: '2025-02-15T00:00:00.000Z',
    endDate: '2025-04-15T23:59:59.999Z',
    lastModifiedBy: 'Nolan Schleifer',
    status: 'Draft',
    modules: [],
    targetPositions: ['pos-5'],
    trainingProvider: 'Internal BCL&D',
    createdBy: {
      id: 'user-5',
      name: 'Nolan Schleifer',
      position: 'Marketing Lead'
    }
  }
];

const OverviewPage: React.FC = () => {
  /* eslint-disable */
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [schedulePlans, setSchedulePlans] = useState<SchedulePlan[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    draft: 0,
    unscheduled: 0
  });
  /* eslint-enable */

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Try to fetch from API
        let plans: SchedulePlan[] = [];
        try {
          const response = await yearlyCalendarService.getSchedulePlans({
            limit: 5 // Get the latest 5 plans
          });
          plans = response.data;
        } catch {
          plans = mockSchedulePlans;
        }

        setSchedulePlans(plans);

        // Calculate stats
        const total = plans.length;
        const completed = plans.filter((plan) => plan.status === 'Completed').length;
        const draft = plans.filter((plan) => plan.status === 'Draft').length;
        const unscheduled = plans.filter((plan) => plan.status === 'Unscheduled').length;

        setStats({
          total,
          completed,
          draft,
          unscheduled
        });
      } catch {
        setSchedulePlans(mockSchedulePlans);

        const total = mockSchedulePlans.length;
        const completed = mockSchedulePlans.filter((plan) => plan.status === 'Completed').length;
        const draft = mockSchedulePlans.filter((plan) => plan.status === 'Draft').length;
        const unscheduled = mockSchedulePlans.filter(
          (plan) => plan.status === 'Unscheduled'
        ).length;

        setStats({
          total,
          completed,
          draft,
          unscheduled
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateCompletionPercentage = () => {
    if (stats.total === 0) return 0;
    return Math.round((stats.completed / stats.total) * 100);
  };

  const handleNavigateToSchedulePlans = () => {
    navigate(pathnames.schedulePlan);
  };

  const handleViewPlan = (id: string) => {
    navigate(pathnames.schedulePlanDetail.replace(':id', id));
  };

  return (
    <Stack gap='md'>
      <PageTitle
        title='Yearly Calendar Overview'
        description='Overview of your learning schedules and educational calendars.'
      />

      <SimpleGrid cols={4}>
        <Paper p='lg' radius='md' withBorder>
          <Stack gap='sm'>
            <Title order={5}>Total Schedules</Title>
            <Group justify='space-between' align='center'>
              <Title order={2}>{stats.total}</Title>
              <IconCalendar size={32} color='#7B2183' />
            </Group>
          </Stack>
        </Paper>

        <Paper p='lg' radius='md' withBorder>
          <Stack gap='sm'>
            <Title order={5}>Completed</Title>
            <Group justify='space-between' align='center'>
              <Title order={2} c='green'>
                {stats.completed}
              </Title>
              <Badge variant='success' styleVariant='subtle'>
                {((stats.completed / (stats.total || 1)) * 100).toFixed(0)}%
              </Badge>
            </Group>
          </Stack>
        </Paper>

        <Paper p='lg' radius='md' withBorder>
          <Stack gap='sm'>
            <Title order={5}>In Draft</Title>
            <Group justify='space-between' align='center'>
              <Title order={2} c='blue'>
                {stats.draft}
              </Title>
              <Badge variant='info' styleVariant='subtle'>
                {((stats.draft / (stats.total || 1)) * 100).toFixed(0)}%
              </Badge>
            </Group>
          </Stack>
        </Paper>

        <Paper p='lg' radius='md' withBorder>
          <Stack gap='sm'>
            <Title order={5}>Unscheduled</Title>
            <Group justify='space-between' align='center'>
              <Title order={2} c='orange'>
                {stats.unscheduled}
              </Title>
              <Badge variant='error' styleVariant='subtle'>
                {((stats.unscheduled / (stats.total || 1)) * 100).toFixed(0)}%
              </Badge>
            </Group>
          </Stack>
        </Paper>
      </SimpleGrid>

      <Grid gutter='lg'>
        <Grid.Col span={8}>
          <Paper p='lg' radius='md' withBorder>
            <Stack gap='md'>
              <Group justify='space-between'>
                <Title order={4}>Recent Schedule Plans</Title>
                <Button
                  variant='subtle'
                  rightSection={<IconArrowRight size={16} />}
                  onClick={handleNavigateToSchedulePlans}
                >
                  View All
                </Button>
              </Group>

              {schedulePlans.length === 0 ? (
                <Text c='dimmed'>No schedule plans available.</Text>
              ) : (
                schedulePlans.map((plan) => (
                  <Paper key={plan.id} p='md' withBorder>
                    <Group justify='space-between'>
                      <Stack gap='xs'>
                        <Title order={5}>{plan.title}</Title>
                        <Group>
                          <Badge
                            variant={
                              plan.status === 'Completed'
                                ? 'success'
                                : plan.status === 'Draft'
                                  ? 'info'
                                  : 'error'
                            }
                            styleVariant='subtle'
                          >
                            {plan.status}
                          </Badge>
                          <Text fz='sm'>
                            {new Date(plan.startDate).toLocaleDateString()} -{' '}
                            {new Date(plan.endDate).toLocaleDateString()}
                          </Text>
                        </Group>
                      </Stack>
                      <Button variant='outline' size='xs' onClick={() => handleViewPlan(plan.id)}>
                        View
                      </Button>
                    </Group>
                  </Paper>
                ))
              )}
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={4}>
          <Paper p='lg' radius='md' withBorder h='100%'>
            <Stack gap='md' align='center' justify='center'>
              <Title order={4}>Overall Completion</Title>

              <RingProgress
                size={180}
                thickness={16}
                roundCaps
                sections={[{ value: calculateCompletionPercentage(), color: 'green' }]}
                label={
                  <Text ta='center' fw={700} size='xl'>
                    {calculateCompletionPercentage()}%
                  </Text>
                }
              />

              <Text ta='center'>
                {stats.completed} out of {stats.total} schedule plans completed
              </Text>

              <Button onClick={handleNavigateToSchedulePlans}>Manage Schedules</Button>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default OverviewPage;
